/**
 * Componente seletor de credenciais para uso em webhooks/APIs
 * Permite selecionar uma credencial salva ou criar uma nova
 * 
 * RELEASE: 1.0.0 - 2024-12-24
 */

import { useState } from 'react';
import { Plus, Key, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTenantCredentials } from '@/hooks/useTenantCredentials';
import { CreateCredentialDialog } from './CreateCredentialDialog';
import { createLogger } from '@/lib/logger';

const log = createLogger('CredentialSelector');

interface CredentialSelectorProps {
  /** ID do tenant atual */
  tenantId: string;
  /** ID da credencial selecionada */
  value?: string;
  /** Callback quando credencial é selecionada */
  onChange: (credentialId: string | undefined) => void;
  /** Label do campo */
  label?: string;
  /** Se o campo é obrigatório */
  required?: boolean;
  /** Classe CSS adicional */
  className?: string;
}

/**
 * Seletor de credenciais salvas para webhooks
 */
export function CredentialSelector({
  tenantId,
  value,
  onChange,
  label = 'Credencial',
  required = false,
  className = '',
}: CredentialSelectorProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const { 
    activeCredentials, 
    isLoading, 
    refetch 
  } = useTenantCredentials(tenantId);

  /**
   * Handler para quando uma nova credencial é criada
   */
  const handleCredentialCreated = (credentialId: string) => {
    log.debug('Nova credencial criada, selecionando:', credentialId);
    onChange(credentialId);
    setShowCreateDialog(false);
    refetch();
  };

  /**
   * Handler para limpar seleção
   */
  const handleClear = () => {
    onChange(undefined);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Key className="h-4 w-4 text-muted-foreground" />
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex gap-2">
        <Select
          value={value || 'none'}
          onValueChange={(v) => onChange(v === 'none' ? undefined : v)}
          disabled={isLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Selecione uma credencial..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">Nenhuma (sem autenticação)</span>
            </SelectItem>
            {activeCredentials.map((cred) => (
              <SelectItem key={cred.id} value={cred.id}>
                <div className="flex items-center gap-2">
                  <Key className="h-3 w-3" />
                  <span>{cred.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({cred.credential_type})
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setShowCreateDialog(true)}
          title="Nova Credencial"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Mensagem quando não há credenciais */}
      {!isLoading && activeCredentials.length === 0 && (
        <Alert variant="default" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Nenhuma credencial cadastrada.{' '}
            <button
              type="button"
              onClick={() => setShowCreateDialog(true)}
              className="underline hover:no-underline font-medium"
            >
              Criar primeira credencial
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Dica de segurança */}
      {value && (
        <p className="text-xs text-muted-foreground">
          🔒 A credencial será injetada automaticamente de forma segura durante a execução.
        </p>
      )}

      {/* Dialog para criar nova credencial */}
      <CreateCredentialDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        tenantId={tenantId}
        onSuccess={handleCredentialCreated}
      />
    </div>
  );
}
